import type { components } from '@/generated/internal-api/openapi-types';
import type { Animal, AnimalApiId } from './Animal.model';
import type { Cage } from './Cage.model';
import type { ApiID, Entity, ID, ISODateTime } from './Common.model';
import type { TeamGlossary, TeamGlossaryApiId } from './Glosary.model';
import type { SampleDefinitionApiId } from './SamplePlan.model';
import type { MetadataFieldValueTypes, MetadataFieldWithValue } from './Metadata.model';
import type { Study } from './Study.model';
import type { TreatmentGroup } from './TreatmentGroup.model';
import type { MainUser } from './User.model';
import type { BenchlingEntityId } from '@/model/Benchling.model';

export type SampleMeasurementKey = 'weight' | 'width' | 'length' | 'depth' | 'volume';
export type SampleDetailV1 = components['schemas']['SampleDetailV1'];
export type SampleDetailKey = SampleDetailV1['key'];
export type SampleDetailValue = SampleDetailV1['value'];
export type SampleDetailUnit = SampleDetailV1['unit'];

export interface SampleMeasurement {
  key?: SampleDetailKey;
  unit: SampleDetailUnit;
  value: SampleDetailValue;
}

export interface SampleType {
  id: ID;
  api_id: TeamGlossaryApiId;
  title: string;
  options: {
    /** keys of measurements that can be captured against a sample type */
    details: Array<SampleMeasurementKey>;
  };
  group?: string;
  group_name?: string;
  description?: unknown;
  type?: string;
  type_name?: string;
}

export interface Sample extends Entity {
  sample_id: string;
  api_id: ApiID<'spl'>;
  comments: string;
  date: ISODateTime;
  collection: Cage;
  type: string;
  user: MainUser;
  study_group?: TreatmentGroup;
  study_link?: Study;
  status: SampleStatus;
  timepoint: string;
  /** list of measurements collected against a sample */
  details: Array<SampleMeasurement>;
  // Payload object for HoT
  meta: Record<string, MetadataFieldValueTypes>;
  metadata: Array<MetadataFieldWithValue>;
  subject: Animal;
  study_id: ID;
  created_by?: Pick<MainUser, 'name' | 'email'>;
  sample_definition_api_id?: SampleDefinitionApiId;
  sample_name?: string;
  reserved_sample_api_id?: string;
  benchling_entity_web_url?: string | null;
  benchling_entity_id?: BenchlingEntityId | null;
  synced_to_benchling?: boolean;
}

export type SampleEdit = components['schemas']['SampleUpdateV1'][];

export interface SampleDetails {
  length?: SampleMeasurement;
  width?: SampleMeasurement;
  weight?: SampleMeasurement;
  volume?: SampleMeasurement;
  depth?: SampleMeasurement;
}

export interface BulkSamplesFormData {
  sample_type: TeamGlossary;
  date: ISODateTime;
  timepoint?: string;
  comments: string;
  details?: SampleDetails;
  meta: Record<string, MetadataFieldValueTypes>;
}

export interface SampleData {
  cageName?: string;
  subject_id: ID;
  subject_api_id: AnimalApiId;
  subjectName: string;
  sampleType: string;
  date: ISODateTime;
  details: Record<string, string>;
  timepoint?: string;
  comments: string;
  meta: Record<string, string>;
}
export type SampleStatus = components['schemas']['SampleBulkUpdateStatus'];

const SAMPLE_STATUS_DISPLAY: Record<SampleStatus, string> = {
  collected: 'Collected',
  cancelled: 'Cancelled',
  planned: 'Planned',
};

// Utility function to get display value for sample status
export const getSampleStatusDisplay = (status: SampleStatus | string): string => {
  return SAMPLE_STATUS_DISPLAY[status as SampleStatus] ?? status ?? '-';
};
